import { RULE_SOURCES } from './sources'

// Single source of truth for every cross-cutting agronomic QUANTITY the plan
// emits, each bound to a RuleSource id. The D2 coverage test asserts every spec
// resolves to a real source and matches its cited value, so a magic number can
// never re-appear uncited. Calculators import these instead of inline literals.

export interface QuantitySpec {
  id: string
  value: number
  unit: string
  /** Topic this quantity asserts; at least one sourceId's appliesTo must cover it. */
  topic: string
  sourceIds: string[]
  note?: string
}

export const QUANTITY_SPECS: Record<string, QuantitySpec> = {
  // Crabgrass pre-emergent broadcast rate (granular product varies; this is the
  // common label rate the plan estimates against).
  preEmergent_lbPer1000: {
    id: 'preEmergent_lbPer1000',
    value: 2.5,
    unit: 'lb product / 1000 sq ft',
    topic: 'pre_emergent_timing',
    sourceIds: ['purdue-preemergent-2026', 'msu-seeding-gdd-2026'],
  },
  // Max nitrogen in a single application (quick-release ceiling). Phase 6 makes
  // this grass/season-aware via singleAppMaxN(); this is the conservative default.
  singleApp_maxN_lbPer1000: {
    id: 'singleApp_maxN_lbPer1000',
    value: 1.0,
    unit: 'lb N / 1000 sq ft',
    topic: 'nitrogen',
    sourceIds: ['purdue-fertilizer-2026', 'pennstate-lawn-care-2026'],
  },
  // Over-application hard-cap multiplier applied to N, P AND K (Phase 6 closes the
  // prior P/K warn-only gap). No nutrient may exceed target * this (1.5x burn/runoff
  // guard, matching the prior nitrogen-only cap — not a zero-headroom cap).
  overApply_capMultiplier: {
    id: 'overApply_capMultiplier',
    value: 1.5,
    unit: 'x target',
    topic: 'nitrogen',
    sourceIds: ['purdue-fertilizer-2026'],
    note: 'Burn/runoff ceiling: no nutrient delivered above 1.5x its agronomic target.',
  },
  gdd_baseTempF: {
    id: 'gdd_baseTempF',
    value: 50,
    unit: '°F',
    topic: 'gdd_base_temp',
    sourceIds: ['msu-seeding-gdd-2026'],
    note: 'Base temperature for cool-season / crabgrass GDD accumulation.',
  },
  gdd_upperCapF: {
    id: 'gdd_upperCapF',
    value: 86,
    unit: '°F',
    topic: 'gdd_base_temp',
    sourceIds: ['msu-seeding-gdd-2026'],
    note: 'Daily high is capped at this for GDD (standard upper threshold).',
  },
  // Crabgrass pre-emergent GDD target (base 50). Soil-temp ~55F crossing is the
  // companion trigger; both cite the same hubs.
  crabgrass_gddTarget: {
    id: 'crabgrass_gddTarget',
    value: 250,
    unit: 'GDD (base 50°F)',
    topic: 'crabgrass_gdd',
    sourceIds: ['msu-seeding-gdd-2026', 'purdue-preemergent-2026'],
  },
  preEmergent_soilTempF: {
    id: 'preEmergent_soilTempF',
    value: 55,
    unit: '°F (rising)',
    topic: 'soil_temperature',
    sourceIds: ['purdue-preemergent-2026'],
  },
  water_gallonsPerSqftInch: {
    id: 'water_gallonsPerSqftInch',
    value: 0.623,
    unit: 'gal / sq ft / inch',
    topic: 'water_quantity',
    sourceIds: ['rutgers-fertilizer-math-2026', 'tamu-watering-2026'],
  },
  // Generic cool-season seeding defaults; per-species rates live in
  // grass-characteristics (cited there). These are the fallbacks.
  seeding_newLawn_lbPer1000: {
    id: 'seeding_newLawn_lbPer1000',
    value: 6,
    unit: 'lb seed / 1000 sq ft',
    topic: 'seeding_rate',
    sourceIds: ['msu-seeding-gdd-2026'],
  },
  seeding_overseed_lbPer1000: {
    id: 'seeding_overseed_lbPer1000',
    value: 3,
    unit: 'lb seed / 1000 sq ft',
    topic: 'seeding_rate',
    sourceIds: ['msu-seeding-gdd-2026', 'pennstate-lawn-care-2026'],
  },
}

export function quantitySpec(id: string): QuantitySpec {
  const spec = QUANTITY_SPECS[id]
  if (!spec) throw new Error(`Unknown quantity spec: ${id}`)
  return spec
}

export function sourceIdsForQuantity(id: string): string[] {
  return quantitySpec(id).sourceIds
}

/** A computed numeric output tagged with whether it traces to a cited source. */
export interface SourcedQuantity<T = number> {
  value: T
  sourced: boolean
  sourceIds: string[]
}

export function makeSourced<T>(value: T, sourceIds: string[]): SourcedQuantity<T> {
  return { value, sourced: sourceIds.length > 0, sourceIds }
}

/** Build a SourcedQuantity from a registry spec's value + sources. */
export function sourcedFromSpec(id: string): SourcedQuantity<number> {
  const spec = quantitySpec(id)
  return makeSourced(spec.value, spec.sourceIds)
}

const KNOWN_SOURCE_IDS = new Set(RULE_SOURCES.map((s) => s.id))
const TOPIC_TO_SOURCES = (() => {
  const map = new Map<string, Set<string>>()
  for (const src of RULE_SOURCES) {
    for (const topic of src.appliesTo) {
      if (!map.has(topic)) map.set(topic, new Set())
      map.get(topic)!.add(src.id)
    }
  }
  return map
})()

/** True when every sourceId on every spec resolves to a real RuleSource. */
export function allQuantitySourcesResolve(): boolean {
  return Object.values(QUANTITY_SPECS).every(
    (spec) => spec.sourceIds.length > 0 && spec.sourceIds.every((id) => KNOWN_SOURCE_IDS.has(id)),
  )
}

/**
 * True when every spec's `topic` is covered by at least one of its OWN sourceIds'
 * appliesTo — so a quantity can't cite a real-but-off-topic source and still pass
 * (mirrors the seasonal-rules topic gate).
 */
export function allQuantityTopicsCovered(): boolean {
  return Object.values(QUANTITY_SPECS).every((spec) => {
    const sourcesForTopic = TOPIC_TO_SOURCES.get(spec.topic)
    if (!sourcesForTopic) return false
    return spec.sourceIds.some((id) => sourcesForTopic.has(id))
  })
}
